/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import {benefitsApiClient, apiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {getBenefitsResponseTransformer} from '#utils/response-transformers/benefits';
import {useUser} from '#contexts/UserContext';
import {ENDPOINTS} from '@/src/utils/api';
import {STALE_TIME, GC_TIME} from '#constants';
import {checkUserRole} from '@/src/utils/cookies';
import {USER_ROLES} from '@/src/utils/constants';

// Hook for uploading benefit image
export function useUploadBenefitImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FormData) => benefitsApiClient.uploadBenefitImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.benefits.all});
    }
  });
}

// Hook for creating benefit
export function useCreateBenefit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.createBenefit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefits.all,
        refetchType: 'inactive'
      });
    }
  });
}

// Hook for fetching merchant benefit by ID
export function useMerchantBenefitById(
  id: string,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'merchant', id, params],
    queryFn: () => benefitsApiClient.getMerchantBenefitById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for fetching member benefit by ID
export function useMemberBenefitById(
  id: string,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'member', id, params],
    queryFn: () => benefitsApiClient.getMemberBenefitById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for editing merchant benefit
export function useEditMerchantBenefit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.updateBenefit(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefits.all,
        refetchType: 'inactive'
      });
    }
  });
}

// Hook for editing merchant benefit locations
export function useEditMerchantBenefitLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.updateBenefitLocations(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefits.all,
        refetchType: 'inactive'
      });
    }
  });
}

// Hook for fetching user benefits
export function useUserBenefit(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'user', params],
    queryFn: () => benefitsApiClient.getUserBenefit(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for fetching redeemed benefits
export function useRedeemedBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'redeemed', params],
    queryFn: () => benefitsApiClient.getRedeemedBenefits(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for fetching expired benefits
export function useExpiredBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'expired', params],
    queryFn: () => benefitsApiClient.getExpiredBenefits(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for redeeming benefits
export function useRedeemBenefit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.redeemBenefit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.benefits.all});
    }
  });
}

// Hook for fetching popular benefits
export function usePopularBenefit(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'popular', params],
    queryFn: () => benefitsApiClient.getPopularBenefit(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options
  });
}

// Hook for fetching benefits (general)
export function useBenefit(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'general', params],
    queryFn: () => benefitsApiClient.getBenefit(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

export function useBenefitById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'by-id', id, params],
    queryFn: () => benefitsApiClient.getBenefitById(String(id), params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for fetching organisation benefit by ID
export function useOrganisationBenefitById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'organisation', id, params],
    queryFn: () => benefitsApiClient.getOrganisationBenefitById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for fetching benefit by ID with role-based endpoint selection
export function useBenefitByIdWithRole(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>,
  isDemo?: boolean
) {
  const {userRole} = useUser();
  const roleFromCookies: string | null = checkUserRole();
  const role = roleFromCookies ?? userRole;

  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'by-id-with-role', id, params, role],
    queryFn: () => {
      // TEMPORARY: Always use member endpoint for testing
      // This should be: /benefit/496/details?organisationId=533
      return isDemo
        ? benefitsApiClient.getBenefitByIdUnauthorized(String(id), params)
        : role == USER_ROLES.member
          ? benefitsApiClient.getMemberBenefitById(String(id), params)
          : benefitsApiClient.getBenefitById(String(id), params);

      // TODO: Restore role-based logic once we confirm this works
      // if (isDemo) {
      //   return benefitsApiClient.getBenefitByIdUnauthorized(String(id), params);
      // }
      // if (role == USER_ROLES.member) {
      //   return benefitsApiClient.getMemberBenefitById(String(id), params);
      // }
      // if (role == USER_ROLES.merchant) {
      //   return benefitsApiClient.getMerchantBenefitById(String(id), params);
      // }
      // if (role == USER_ROLES.organisation) {
      //   return benefitsApiClient.getOrganisationBenefitById(String(id), params);
      // }
      // return benefitsApiClient.getBenefitById(String(id), params);
    },
    staleTime: 0, // No caching to ensure fresh data
    gcTime: 0, // No caching to ensure fresh data
    enabled: !!id,
    ...options
  });
}

// Hook for fetching organisation benefits
export function useOrganisationBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'organisation', params],
    queryFn: async () => {
      const response = await benefitsApiClient.getOrganisationBenefits(params);
      return getBenefitsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for fetching merchant benefits
export function useMerchantBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'merchant', params],
    queryFn: async () => {
      const response = await benefitsApiClient.getMerchantBenefits(params);
      return getBenefitsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for searching organisation benefits
export function useSearchOrganisationBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'search', 'organisation', params],
    queryFn: async () => {
      const response = await benefitsApiClient.getOrganisationBenefits(params);
      return getBenefitsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for searching merchant benefits
export function useSearchMerchantBenefits(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'search', 'merchant', params],
    queryFn: async () => {
      const response = await benefitsApiClient.getMerchantBenefits(params);
      return getBenefitsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

// Hook for updating organisation benefit status
export function useUpdateOrganisationBenefitStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.updateOrganisationBenefitStatus(data),
    onSuccess: (_, variables) => {
      // Only invalidate specific benefit and organisation benefits list
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.benefits.detail(variables.id)
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefits.organisation()
      });
    }
  });
}

// Hook for updating merchant benefit status
export function useUpdateMerchantBenefitStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      benefitsApiClient.updateMerchantBenefitStatus(data),
    onSuccess: (_, variables) => {
      // Only invalidate specific benefit and merchant benefits list
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.benefits.detail(variables.id)
        });
      }
      queryClient.invalidateQueries({queryKey: queryKeys.benefits.merchant()});
    }
  });
}

// Hook for fetching benefit availability
export function useBenefitAvailability(
  id: string,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'availability', id],
    queryFn: () => benefitsApiClient.getBenefitAvailability(id),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for fetching benefit distribution CSV
export function useBenefitDistribution(
  id: string | number,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'distribution', id],
    queryFn: () => benefitsApiClient.getBenefitDistribution(String(id)),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

// Hook for updating benefit availability
export function useUpdateBenefitAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {id: string; organisations: any[]}) =>
      benefitsApiClient.updateBenefitAvailability(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.benefits.all});
    }
  });
}

// Hook for distributing benefit to all organisations by name
export function useDistributeToAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (benefitName: string) =>
      apiClient.get(ENDPOINTS.benefit.distributeToAll, {
        params: {name: benefitName}
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.benefits.all});
    }
  });
}

// Hooks for fetching benefit statistics

export function useMerchantBenefitsStatistics(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'statistic', 'merchant', params],
    queryFn: () => benefitsApiClient.getMerchantBenefitsStatistics(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

export function useMerchantBenefitsStatisticsOverall(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.benefits.all,
      'statistic_overall',
      'merchant',
      params
    ],
    queryFn: () =>
      benefitsApiClient.getMerchantBenefitsStatisticsOverall(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}

export function useMerchantBenefitStatisticById(
  id: string,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'statistic', 'merchant', id, params],
    queryFn: () =>
      benefitsApiClient.getMerchantBenefitStatisticById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

export function useMerchantBenefitRedemptionRateById(
  id: string,
  params: {
    startDate?: Date | string;
    endDate?: Date | string;
    id: number | string;
  },
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.benefits.all,
      'statistic_redemption_rate',
      'merchant',
      id,
      params
    ],
    queryFn: () =>
      benefitsApiClient.getMerchantBenefitRedemptionRateById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

export function useMerchantBenefitTotalRedemptionsById(
  id: string,
  params: {
    startDate?: Date | string;
    endDate?: Date | string;
    id: number | string;
  },
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [
      ...queryKeys.benefits.all,
      'statistic_total_redemptions',
      'merchant',
      id,
      params
    ],
    queryFn: () =>
      benefitsApiClient.getMerchantBenefitTotalRedemptionsById(id, params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    enabled: !!id,
    ...options
  });
}

export function useBenefitsGlobalSearch(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.benefits.all, 'global-search', params],
    queryFn: () => benefitsApiClient.getGlobalSearchBenefits(params),
    staleTime: STALE_TIME.BENEFITS,
    gcTime: GC_TIME.BENEFITS,
    ...options
  });
}
